import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '../components/Icon';
import { PageHead } from '../components/PageHead';
import { absolute, api, errorText } from '../lib/api';
import { useAuth } from '../lib/auth';
import { clock } from '../lib/format';
import type { EpisodeSummary } from '../types';

type LibEpisode = EpisodeSummary & { mediaKind: string; segments: number; translated: number };
interface FeedItem {
  guid: string;
  title: string;
  url: string;
  mediaType: 'audio' | 'video';
  publishedOn: string | null;
  durationMs: number | null;
}
interface Feed {
  sourceId: string;
  title: string;
  items: FeedItem[];
}
interface Library {
  plan: string;
  usage: { used: number; limit: number; period: string };
  episodes: LibEpisode[];
  sources: { id: string; url: string; title: string | null }[];
}
type Filter = 'all' | 'ready' | 'needs' | 'busy';
type Tab = 'file' | 'url' | 'rss';

function statusOf(e: LibEpisode): { label: string; ready: boolean; filter: Filter } {
  if (e.status === 'uploading' || e.status === 'processing') return { label: e.statusDetail ?? '处理中', ready: false, filter: 'busy' };
  if (e.status === 'error') return { label: '处理出错', ready: false, filter: 'needs' };
  if (e.segments === 0) return { label: '待添加字幕', ready: false, filter: 'needs' };
  if (e.translated < e.segments) return { label: '英文字幕', ready: true, filter: 'ready' };
  return { label: '双语', ready: true, filter: 'ready' };
}

function mediaDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const el = document.createElement(file.type.startsWith('video') ? 'video' : 'audio');
    const url = URL.createObjectURL(file);
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : undefined);
    };
    el.onerror = () => resolve(undefined);
    el.src = url;
  });
}

/** Covers for items without artwork: a warm gradient with the first letters of the title. */
const PALETTES = [
  ['#dc8866', '#b55236'],
  ['#796070', '#432e3c'],
  ['#9bae8f', '#596c53'],
  ['#f1c49d', '#d98961'],
];
function Cover({ title, src, size = 'row' }: { title: string; src?: string | null; size?: 'row' | 'card' }) {
  if (src) return <img className={`cover ${size}`} src={absolute(src)} alt="" />;
  const n = [...title].reduce((a, c) => a + c.charCodeAt(0), 0);
  const [a, b] = PALETTES[n % PALETTES.length]!;
  const letters = title.replace(/[^A-Za-z一-鿿]/g, '').slice(0, 2).toUpperCase() || 'EN';
  return (
    <div className={`cover ${size} generated`} style={{ background: `linear-gradient(145deg, ${a}, ${b})` }} aria-hidden="true">
      {letters}
    </div>
  );
}

const MEDIA_ACCEPT = '.mp3,.m4a,.aac,.wav,.mp4,.m4v,.mov,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,video/mp4,video/quicktime';
const EXT_TYPES: Record<string, string> = { mp3: 'audio/mpeg', m4a: 'audio/mp4', aac: 'audio/aac', wav: 'audio/wav', mp4: 'video/mp4', m4v: 'video/mp4', mov: 'video/quicktime' };
/** Some pickers report no MIME type; fall back to the file extension. */
const mediaType = (f: File) => (/^(audio|video)\//.test(f.type) ? f.type : (EXT_TYPES[f.name.split('.').pop()?.toLowerCase() ?? ''] ?? 'audio/mpeg'));

export function LibraryPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [lib, setLib] = useState<Library | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [dialogTab, setDialogTab] = useState<Tab | null>(null);
  const [feed, setFeed] = useState<Feed | null>(null);

  const load = useCallback(() => api<Library>('/library').then(setLib, (e) => setMsg(errorText(e))), []);
  useEffect(() => {
    if (me?.user) void load();
  }, [me?.user, load]);
  // Poll while something is uploading or being processed.
  useEffect(() => {
    if (!lib?.episodes.some((e) => e.status === 'processing' || e.status === 'uploading')) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [lib, load]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setMsg(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setMsg(errorText(e));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const episodes = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (lib?.episodes ?? []).filter((e) => (filter === 'all' || statusOf(e).filter === filter) && (!q || e.title.toLowerCase().includes(q)));
  }, [lib, filter, query]);

  if (!me) return <p className="page muted">加载中…</p>;
  if (!me.user) return <p className="page muted">需要登录后使用资料库。</p>;

  const uploads = lib?.episodes.filter((e) => e.mediaKind === 'r2').length ?? 0;
  const links = lib?.episodes.filter((e) => e.mediaKind === 'remote').length ?? 0;
  const usage = lib?.usage;
  const minutes = (s: number) => Math.round(s / 60);

  const importSubtitles = (ep: LibEpisode, file: File) =>
    run('导入字幕', async () => {
      const r = await api<{ segments: number; translated: number }>(`/library/episodes/${ep.id}/subtitles`, { method: 'POST', json: { text: await file.text(), filename: file.name } });
      setMsg(`已导入 ${r.segments} 句字幕${r.translated ? `，其中 ${r.translated} 句有中文` : ''}`);
    });

  return (
    <div className="page library">
      <PageHead eyebrow="A WORLD OF REAL VOICES" title="资料库。" sub="播客、访谈、自己的录音，都可以成为下一课。">
        <button className="primary add-button" onClick={() => setDialogTab('file')}>
          <Icon name="plus" />
          添加学习内容
        </button>
      </PageHead>

      {(msg || busy) && <p className="status">{busy ? `${busy}…` : msg}</p>}

      <div className="show-grid">
        <button className="card show-card" onClick={() => setDialogTab('file')}>
          <div className="cover card generated upload-cover">
            <Icon name="folder" />
          </div>
          <h3>上传的文件</h3>
          <small>{uploads} 个</small>
        </button>
        <button className="card show-card" onClick={() => setDialogTab('url')}>
          <div className="cover card generated link-cover">
            <Icon name="link" />
          </div>
          <h3>链接与播客单集</h3>
          <small>{links} 个</small>
        </button>
        {lib?.sources.map((s) => (
          <button key={s.id} className="card show-card" onClick={() => run('读取播客', async () => (setFeed(await api<Feed>(`/library/sources/${s.id}`)), setDialogTab('rss')))}>
            <Cover title={s.title ?? s.url} size="card" />
            <h3>{s.title ?? s.url}</h3>
            <small>播客订阅 · 查看单集</small>
          </button>
        ))}
        <button className="card show-card add-card" onClick={() => setDialogTab('rss')}>
          <div className="cover card add-cover">
            <Icon name="rss" />
          </div>
          <h3>订阅播客</h3>
          <small>粘贴 RSS 地址</small>
        </button>
      </div>

      {usage && (
        <div className="quota">
          <div className="row spread">
            <span>AI 字幕与翻译额度</span>
            <small className="muted">
              {minutes(usage.used)} / {minutes(usage.limit)} 分钟{usage.period === 'trial' ? '（体验）' : '（本月）'}
            </small>
          </div>
          <div className="quota-bar">
            <i style={{ width: `${Math.min(100, (usage.used / Math.max(1, usage.limit)) * 100)}%` }} />
          </div>
          <small className="muted">自带字幕（SRT / VTT / TTML）的内容不占额度。</small>
        </div>
      )}

      <div className="row spread wrap section-title">
        <h2>
          所有单集 <small>{episodes.length}</small>
        </h2>
        <input className="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索单集" aria-label="搜索单集" />
      </div>
      <div className="chips">
        {(
          [
            ['all', '全部'],
            ['ready', '可学习'],
            ['needs', '待字幕'],
            ['busy', '处理中'],
          ] as [Filter, string][]
        ).map(([id, label]) => (
          <button key={id} className={`chip${filter === id ? ' on' : ''}`} onClick={() => setFilter(id)}>
            {label}
          </button>
        ))}
      </div>

      <div className="episode-list">
        {lib && episodes.length === 0 && (
          <div className="empty">
            <h3>{lib.episodes.length ? '没有符合条件的单集' : '还没有自己的学习内容'}</h3>
            {!lib.episodes.length && (
              <button className="primary" onClick={() => setDialogTab('file')}>
                <Icon name="plus" />
                添加第一个
              </button>
            )}
          </div>
        )}
        {episodes.map((ep) => {
          const st = statusOf(ep);
          const open = openRow === ep.id;
          const idle = ep.status !== 'uploading' && ep.status !== 'processing';
          return (
            <article key={ep.id} className={`episode-row${open ? ' open' : ''}`}>
              <Cover title={ep.title} src={ep.posterUrl} />
              <div className="episode-main" onClick={() => setOpenRow(open ? null : ep.id)}>
                <h3>{ep.title}</h3>
                <p>
                  {ep.mediaKind === 'r2' ? '上传' : '链接'}
                  {ep.durationMs ? ` · ${clock(ep.durationMs)}` : ''}
                  {ep.segments ? ` · ${ep.segments} 句` : ''}
                </p>
              </div>
              <div className="row tight">
                <span className={`tag${st.ready ? ' ready' : ''}`}>{st.label}</span>
                {st.ready ? (
                  <button className="icon play-round" aria-label={`学习 ${ep.title}`} onClick={() => navigate(`/?ep=${ep.id}`)}>
                    <Icon name="play" />
                  </button>
                ) : (
                  <button className="icon plain" aria-label="更多操作" onClick={() => setOpenRow(open ? null : ep.id)}>
                    <Icon name="more" />
                  </button>
                )}
              </div>
              {open && idle && (
                <div className="row-actions">
                  <label className="button small">
                    导入字幕
                    <input type="file" accept=".srt,.vtt,.ttml,.xml,.json" hidden onChange={(e) => e.target.files?.[0] && importSubtitles(ep, e.target.files[0])} />
                  </label>
                  {(ep.segments === 0 || ep.translated < ep.segments) && (
                    <button className="small" onClick={() => run('提交 AI 处理', () => api(`/library/episodes/${ep.id}/process`, { method: 'POST', json: { quality: 'standard', durationMs: ep.durationMs ?? undefined } }))}>
                      {ep.segments === 0 ? 'AI 生成字幕和翻译' : 'AI 补全中文'}
                    </button>
                  )}
                  <button className="small plain danger" onClick={() => confirm(`删除「${ep.title}」？相关生词的例句也会删除。`) && run('删除', () => api(`/library/episodes/${ep.id}`, { method: 'DELETE' }))}>
                    <Icon name="trash" />
                    删除
                  </button>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {dialogTab && (
        <ImportDialog
          tab={dialogTab}
          feed={feed}
          busy={busy}
          onTab={setDialogTab}
          onFeed={setFeed}
          onClose={() => {
            setDialogTab(null);
            setFeed(null);
          }}
          run={run}
        />
      )}
    </div>
  );
}

function ImportDialog({
  tab,
  feed,
  busy,
  onTab,
  onFeed,
  onClose,
  run,
}: {
  tab: Tab;
  feed: Feed | null;
  busy: string | null;
  onTab(t: Tab): void;
  onFeed(f: Feed | null): void;
  onClose(): void;
  run(label: string, fn: () => Promise<unknown>): Promise<boolean>;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [autoAsr, setAutoAsr] = useState(true);
  const [progress, setProgress] = useState<string | null>(null);
  const [link, setLink] = useState({ url: '', title: '' });
  const [rss, setRss] = useState('');
  const [added, setAdded] = useState<Set<string>>(new Set());
  useEffect(() => {
    ref.current?.showModal();
  }, []);

  const upload = async () => {
    if (!file) return;
    const ok = await run('上传', async () => {
      const durationMs = await mediaDuration(file);
      const init = await api<{ episodeId: string; partSize: number; parts: number }>('/library/uploads', {
        method: 'POST',
        json: { title: file.name.replace(/\.[^.]+$/, ''), filename: file.name, contentType: mediaType(file), size: file.size, durationMs },
      });
      const parts: { partNumber: number; etag: string }[] = [];
      for (let n = 1; n <= init.parts; n++) {
        setProgress(`上传中 ${Math.round(((n - 1) / init.parts) * 100)}%`);
        parts.push(await api(`/library/uploads/${init.episodeId}/parts/${n}`, { method: 'PUT', body: file.slice((n - 1) * init.partSize, n * init.partSize) }));
      }
      await api(`/library/uploads/${init.episodeId}/complete`, { method: 'POST', json: { parts } });
      // Start transcription straight away unless the learner will bring their own subtitles.
      if (autoAsr) {
        setProgress('提交 AI 字幕…');
        await api(`/library/episodes/${init.episodeId}/process`, { method: 'POST', json: { quality: 'standard', durationMs } });
      }
    });
    setProgress(null);
    if (ok) onClose();
  };

  return (
    <dialog ref={ref} className="import-dialog" onClose={onClose} onClick={(e) => e.target === ref.current && onClose()}>
      <div className="dialog-head">
        <h2>把喜欢的声音带进来</h2>
        <button className="icon close-ring" aria-label="关闭" onClick={onClose}>
          <Icon name="close" />
        </button>
      </div>
      <p className="muted">真实播客、访谈、自己的录音，都可以成为下一课。</p>
      <div className="segmented">
        {(
          [
            ['file', '音视频文件'],
            ['url', '媒体链接'],
            ['rss', '播客 RSS'],
          ] as [Tab, string][]
        ).map(([t, label]) => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => onTab(t)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'file' && (
        <>
          <label className="file-zone">
            <Icon name="headphones" />
            <p>{file ? file.name : '选择设备里的 MP4、MP3、M4A 或 WAV'}</p>
            {/* iOS greys out mp3 for a bare audio/* filter, so list the formats explicitly. */}
            <input type="file" accept={MEDIA_ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <small>单个文件最多 1 GB</small>
          </label>
          <label className="check-row">
            <input type="checkbox" checked={autoAsr} onChange={(e) => setAutoAsr(e.target.checked)} />
            上传后自动生成英文字幕（有自己的字幕文件可取消，稍后导入）
          </label>
          <div className="actions">
            <button className="primary" disabled={!file || Boolean(busy)} onClick={upload}>
              {progress ?? (busy ? `${busy}…` : '导入文件')}
            </button>
          </div>
        </>
      )}

      {tab === 'url' && (
        <form
          className="stack import-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await run('添加链接', () =>
              api('/library/remote', {
                method: 'POST',
                json: { url: link.url, title: link.title || link.url.split('/').pop() || '我的音频', mediaType: /\.(mp4|webm|mov|m4v)(\?|$)/i.test(link.url) ? 'video' : 'audio' },
              }),
            );
            if (ok) onClose();
          }}
        >
          <label>
            音视频直链（MP4 / MP3 / M4A）
            <input type="url" required value={link.url} onChange={(e) => setLink({ ...link, url: e.target.value })} placeholder="https://…" />
          </label>
          <label>
            内容标题
            <input value={link.title} onChange={(e) => setLink({ ...link, title: e.target.value })} placeholder="给这段声音起个名字" />
          </label>
          <div className="actions">
            <button className="primary" disabled={Boolean(busy)}>
              添加音频
            </button>
          </div>
        </form>
      )}

      {tab === 'rss' && (
        <>
          {!feed ? (
            <form
              className="stack import-form"
              onSubmit={(e) => {
                e.preventDefault();
                void run('读取播客', async () => onFeed(await api<Feed>('/library/sources', { method: 'POST', json: { url: rss } })));
              }}
            >
              <label>
                播客 RSS 订阅地址
                <input type="url" required value={rss} onChange={(e) => setRss(e.target.value)} placeholder="https://…" />
              </label>
              <p className="source-note">粘贴节目发布方的 RSS，之后可以从最近的单集里挑选加入。</p>
              <div className="actions">
                <button className="primary" disabled={Boolean(busy)}>
                  {busy ? `${busy}…` : '读取节目'}
                </button>
              </div>
            </form>
          ) : (
            <div className="feed">
              <div className="row spread">
                <b>{feed.title}</b>
                <button className="plain small" onClick={() => onFeed(null)}>
                  换一个
                </button>
              </div>
              <ul>
                {feed.items.slice(0, 40).map((it) => (
                  <li key={it.guid}>
                    <div>
                      <span>{it.title}</span>
                      <small className="muted">
                        {it.publishedOn ?? ''}
                        {it.durationMs ? ` · ${clock(it.durationMs)}` : ''}
                      </small>
                    </div>
                    <button
                      className={`small${added.has(it.guid) ? ' secondary' : ''}`}
                      disabled={added.has(it.guid)}
                      onClick={async () => {
                        const ok = await run('加入单集', () =>
                          api('/library/remote', { method: 'POST', json: { url: it.url, title: it.title, mediaType: it.mediaType, publishedOn: it.publishedOn, durationMs: it.durationMs } }),
                        );
                        if (ok) setAdded((s) => new Set(s).add(it.guid));
                      }}
                    >
                      {added.has(it.guid) ? '已加入' : '加入'}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      {busy && <p className="form-error">{busy}…</p>}
    </dialog>
  );
}
